import Vue from 'vue';
import Router, { Route } from 'vue-router';
import { requireAuth } from './utils';

Vue.use(Router);

export default new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    { path: '/', redirect: '/content' },
    {
      path: '/login',
      name: 'Login',
      component: () =>
        import(/* webpackChunkName: "login" */ '@/views/User/Login.vue'),
    },
    {
      path: '/secure',
      name: 'secure',
      component: () =>
        import(/* webpackChunkName: "secure" */ '@/layouts/Secure.vue'),
      children: [
        {
          path: '/blocks',
          name: 'blocks',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "blocks" */ '@/views/Block/BlockList.vue'),
        },
        {
          path: '/blocks/editor',
          name: 'new-block',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "blocks" */ '@/views/Block/BlockEditor.vue'),
        },
        {
          path: '/blocks/editor/:id',
          name: 'edit-block',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "blocks" */ '@/views/Block/BlockEditor.vue'),
          props: (route: Route): object => {
            return {
              id: route.params.id,
            };
          },
        },
        {
          path: '/builds',
          name: 'builds',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "build-management" */ '@/views/Build/BuildManagement.vue'),
        },
        {
          path: '/categories',
          name: 'categories',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "category-list" */ '@/views/Category/CategoryList.vue'),
        },
        {
          path: '/categories/editor',
          name: 'new-category',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "category-editor" */ '@/views/Category/CategoryEditor.vue'),
        },
        {
          path: '/categories/editor/:id',
          name: 'edit-category',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "category-editor" */ '@/views/Category/CategoryEditor.vue'),
          props: (route: Route): object => {
            return {
              id: route.params.id,
            };
          },
        },
        {
          path: '/content',
          name: 'content',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "content-list" */ '@/views/Content/ContentList.vue'),
          props: { categorySlug: undefined },
        },
        {
          path: '/content/editor',
          name: 'editor-new',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "content-editor" */ '@/views/Content/ContentEditor.vue'),
        },
        {
          path: '/content/editor/:id',
          name: 'editor',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "content-editor" */ '@/views/Content/ContentEditor.vue'),
          props: (route: Route): object => {
            return {
              id: route.params.id,
            };
          },
        },
        {
          path: '/content/:category',
          name: 'content-category',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "content-list" */ '@/views/Content/ContentList.vue'),
          props: (route: Route): object => {
            return {
              categorySlug: route.params.category,
            };
          },
        },
        {
          path: '/funders',
          name: 'funders',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "funder-list" */ '@/views/Funder/FunderList.vue'),
        },
        {
          path: '/products',
          name: 'all-products',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "products-list" */ '@/views/Product/ProductList.vue'),
          props: { funderSlug: undefined },
        },
        {
          path: '/products/:funder/:status',
          name: 'products',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "products-list" */ '@/views/Product/ProductList.vue'),
          props: (route: Route): object => {
            return {
              funderSlug: route.params.funder,
              published: route.params.status === 'published',
            };
          },
        },
        {
          path: '/product/editor/:id',
          name: 'edit-product',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "product-editor" */ '@/views/Product/ProductEditor.vue'),
          props: (route: Route): object => {
            return {
              id: route.params.id,
            };
          },
        },
        {
          path: '/images',
          name: 'image-manager',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "images" */ '@/views/Images/ImageManager.vue'),
        },
        {
          path: '/menus',
          name: 'menus',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "menu-list" */ '@/views/Menu/MenuList.vue'),
        },
        {
          path: '/menus/editor',
          name: 'new-menu',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "menu-editor" */ '@/views/Menu/MenuEditor.vue'),
        },
        {
          path: '/menus/editor/:id',
          name: 'edit-menu',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "menu-editor" */ '@/views/Menu/MenuEditor.vue'),
          props: (route: Route): object => {
            return {
              id: route.params.id,
            };
          },
        },
        {
          path: '/pages',
          name: 'pages',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "page-list" */ '@/views/Page/PageList.vue'),
        },
        {
          path: '/pages/editor',
          name: 'new-page',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "page-editor" */ '@/views/Page/PageEditor.vue'),
        },
        {
          path: '/pages/editor/:id',
          name: 'edit-page',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "page-editor" */ '@/views/Page/PageEditor.vue'),
          props: (route: Route): object => {
            return {
              id: route.params.id,
            };
          },
        },
        {
          path: '/redirects',
          name: 'redirects',
          beforeEnter: requireAuth,
          component: () =>
            import(/* webpackChunkName: "redirects" */ '@/views/Redirect/RedirectManager.vue'),
        },
      ],
    },
  ],
});
